import { useQuery } from '@tanstack/react-query';
import fetchAPI from '../fetchAPI';

/**
 * Custom roles for SalesRook staff (server: `/custom-roles` and
 * `GET /user/access`). A Super Admin creates roles, grants each one dashboard
 * sections at view or edit, and adds staff on them.
 */
export type SectionLevel = 'view' | 'edit';
export interface SectionGrant {
  section: string;
  level: SectionLevel;
}
export interface SectionInfo {
  key: string;
  label: string;
  group: string;
  /** False for read-only sections, which can only be granted at view. */
  editable: boolean;
}
export interface CustomRole {
  id: string;
  name: string;
  sections: SectionGrant[];
  staffCount: number;
}
export interface StaffMember {
  id: string;
  name: string;
  email: string;
  active: boolean;
  roleId: string;
  roleName: string;
  /** False for Super Admins, who can't be changed from the Roles page. */
  editable: boolean;
}
export interface MyAccess {
  role: string;
  isPlatformUser: boolean;
  sections: SectionGrant[];
}

type Envelope<T> = iResponse<{ code: number; data?: T; message?: string }>;
const ROUTE = 'custom-roles';

export const callGetSections = (): Promise<Envelope<SectionInfo[]>> =>
  fetchAPI({ method: 'GET', route: `${ROUTE}/sections` });
export const callGetCustomRoles = (): Promise<Envelope<CustomRole[]>> =>
  fetchAPI({ method: 'GET', route: ROUTE });
export const callSaveCustomRole = (
  payload: { name: string; sections: SectionGrant[] },
  id?: string
): Promise<Envelope<CustomRole>> =>
  id
    ? fetchAPI({ method: 'PUT', route: ROUTE, id, data: payload as unknown as APIData })
    : fetchAPI({ method: 'POST', route: ROUTE, data: payload as unknown as APIData });
export const callDeleteCustomRole = (id: string): Promise<Envelope<never>> =>
  fetchAPI({ method: 'DELETE', route: ROUTE, id });

export const callGetStaff = (): Promise<Envelope<StaffMember[]>> =>
  fetchAPI({ method: 'GET', route: `${ROUTE}/staff` });
export const callAddStaff = (payload: {
  name: string;
  email: string;
  roleId: string;
}): Promise<Envelope<StaffMember>> =>
  fetchAPI({ method: 'POST', route: `${ROUTE}/staff`, data: payload as unknown as APIData });
export const callChangeStaffRole = (
  userId: string,
  roleId: string
): Promise<Envelope<StaffMember>> =>
  fetchAPI({
    method: 'PATCH',
    route: `${ROUTE}/staff`,
    id: userId,
    data: { roleId } as unknown as APIData
  });
export const callRemoveStaff = (userId: string): Promise<Envelope<never>> =>
  fetchAPI({ method: 'DELETE', route: `${ROUTE}/staff`, id: userId });

/**
 * The signed-in user's sections. A Super Admin gets every section at edit. Kept
 * per user for the session (staleTime: Infinity) — a role change shows after a
 * reload, while the server applies it within 30 seconds either way. Keyed by
 * the user's id so a different sign-in on the same tab never reuses it.
 */
export const useMyAccess = (userId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['user/access', userId],
    enabled: enabled && Boolean(userId),
    staleTime: Infinity,
    queryFn: async (): Promise<MyAccess> => {
      const response = await fetchAPI<MyAccess>({ method: 'GET', route: 'user/access' });
      if (!response.data) throw new Error('Could not load access.');
      return response.data;
    }
  });
